/**
 * Home Appraisal Section Switch
 * 
 * Control: Current implementation (PropertyAppraisal with renderTitle)
 * Variation: New design using ContentSectionTemplate
 */

import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { Box } from '@mui/material';
import dynamic from 'next/dynamic';
import { InView } from 'react-intersection-observer';
import { isZoocasaTenant, TenantId } from '@tenants';
import { AllowedExperimentKey } from '@tenants/types';
import { AnalyticsController } from 'analytics/analytics-controller';
import { GTM_VIEW_ADDRESS_PAGE_ZOOPRAISAL, GTM_MORTGAGE_CALC_BANNER_CLICK } from 'constants/events';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { ExperimentalSection } from 'ui/experimental-section';
import { MortgageCalculatorCallout } from 'ui/mortgage-calculator-callout';
import { ContentSectionTemplate } from 'components/atomic/templates/content-section';
import { Zoopraisal } from 'data/zoopraisal';
import { SellingRange } from 'data/zoopraisal/types';
import LoadingText from 'components/loading-text';

const PropertyAppraisal = dynamic(
  () => import('components/pages/address/public/components/property-appraisal'),
  { loading: () => <LoadingText /> }
);

// ============================================================================
// Types
// ============================================================================

export interface HomeAppraisalSectionProps {
  /** Section ID for scroll targeting */
  id: string;
  /** Tenant ID for localization context */
  tenantId: TenantId;
  /** Tenant theme name */
  tenantName: string;
  /** Listing price */
  price: number;
  /** Days on market */
  daysOnMarket: number | null;
  /** Whether content should be blurred/private */
  isPrivate: boolean;
  /** Whether to scramble content */
  shouldScramble: boolean;
  /** Appraisal result data */
  appraisalResult: Zoopraisal;
  /** Selling range data */
  sellingRange?: SellingRange;
  /** IDX compliance: hide AVM selling range estimate */
  hideAvm?: boolean;
  /** Callback for contact agent click */
  onContactAnAgentClick?: () => void;
}

// ============================================================================
// Shared Hook
// ============================================================================

function useAppraisalTracking() {
  return useCallback((isVisible: boolean) => {
    AnalyticsController.trackEventConditionally(
      { event: GTM_VIEW_ADDRESS_PAGE_ZOOPRAISAL },
      isVisible
    );
  }, []);
}

// ============================================================================
// Sub-components (for Control's legacy title)
// ============================================================================

function AppraisalTitle({ tenantId }: { tenantId: TenantId }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const context = isZoocasaTenant(tenantId) ? 'zoopraisal' : undefined;
  return (
    <h2>
      {t(LOCALE_COMMON_PATHS.ADDRESS_HOME_APPRAISAL_TITLE, {
        defaultValue: 'Home Estimate Zoopraisal®',
        context,
      })}
    </h2>
  );
}

const MORTGAGE_CALCULATOR_CTA_HREF = '/mortgage-calculator?utm_source=zoopraisal_callout';

export function MortgageCtaBanner() {
  const { t } = useTranslation(Namespaces.COMMON);
  const handleClick = useCallback(() => {
    AnalyticsController.trackEvent(GTM_MORTGAGE_CALC_BANNER_CLICK);
  }, []);

  return (
    <ExperimentalSection
      experimentKey={AllowedExperimentKey.ZOOPRAISAL_MORTGAGE_CTA}
      control={null}
      variation={
        <MortgageCalculatorCallout
          href={MORTGAGE_CALCULATOR_CTA_HREF}
          label={t(LOCALE_COMMON_PATHS.ADDRESS_MORTGAGE_CALCULATOR_CTA_LABEL, {
            defaultValue: 'How Much House Can I Afford?',
          })}
          onClick={handleClick}
        />
      }
    />
  );
}

// ============================================================================
// Control (Current Implementation)
// ============================================================================

function HomeAppraisalSectionControl({
  id,
  tenantId,
  tenantName,
  price,
  daysOnMarket,
  isPrivate,
  shouldScramble,
  appraisalResult,
  sellingRange,
  hideAvm,
  onContactAnAgentClick,
}: HomeAppraisalSectionProps) {
  const trackZoopraisalView = useAppraisalTracking();

  return (
    <Box component="section" id={id} sx={{ scrollMarginTop: 'var(--scroll-offset, 150px)' }}>
      <InView onChange={trackZoopraisalView} triggerOnce>
        <PropertyAppraisal
          tenantName={tenantName}
          agentDisclaimerLabel=""
          renderTitle={() => (
            <>
              <MortgageCtaBanner />
              <AppraisalTitle tenantId={tenantId} />
            </>
          )}
          price={price}
          daysOnMarket={daysOnMarket}
          isPrivate={isPrivate}
          shouldScramble={shouldScramble}
          appraisal={appraisalResult}
          sellingRange={sellingRange}
          hideAvm={hideAvm}
          onContactAnAgentClick={onContactAnAgentClick}
        />
      </InView>
    </Box>
  );
}

// ============================================================================
// Variation (New Design with Templates)
// ============================================================================

function HomeAppraisalSectionVariation({
  id,
  tenantId,
  tenantName,
  price,
  daysOnMarket,
  isPrivate,
  shouldScramble,
  appraisalResult,
  sellingRange,
  hideAvm,
  onContactAnAgentClick,
}: HomeAppraisalSectionProps) {
  const trackZoopraisalView = useAppraisalTracking();

  return (
    <ContentSectionTemplate id={id} hideHeading withCard={false}>
      <InView onChange={trackZoopraisalView} triggerOnce>
        <PropertyAppraisal
          tenantName={tenantName}
          agentDisclaimerLabel=""
          renderTitle={() => (
            <>
              <MortgageCtaBanner />
              <AppraisalTitle tenantId={tenantId} />
            </>
          )}
          price={price}
          daysOnMarket={daysOnMarket}
          isPrivate={isPrivate}
          shouldScramble={shouldScramble}
          appraisal={appraisalResult}
          sellingRange={sellingRange}
          hideAvm={hideAvm}
          onContactAnAgentClick={onContactAnAgentClick}
        />
      </InView>
    </ContentSectionTemplate>
  );
}

// ============================================================================
// Switch
// ============================================================================

export function HomeAppraisalSectionSwitch(props: HomeAppraisalSectionProps) {
  return (
    <ExperimentalSection
      experimentKey={AllowedExperimentKey.STICKY_HEADER_TABS}
      control={<HomeAppraisalSectionControl {...props} />}
      variation={<HomeAppraisalSectionVariation {...props} />}
    />
  );
}
